import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  saveKycInfo,
  selectApplication,
  selectAppSaving,
  selectAppError,
  clearApplicationError
} from '../../../redux/slices/applicationSlice';
import {
  StepCard,
  StepActions,
  Button,
  FieldGrid,
  SectionRule
} from '../../../components/stepCard';
import { Field, TextField, SelectField } from '../../../components/formField';
import ImportPanel from '../../../components/importPanel';
import { isValidAadhaar } from '../../../utils/aadhaar';
import PageTransition from '../../../components/pageTransition';
import { notify } from '../../../utils/notify';
import { selectExtractedFields } from '../../../redux/slices/applicationSlice';
import './index.scss';

const EMPLOYMENT_OPTIONS = [
  { value: 'SALARIED', label: 'Salaried' },
  { value: 'SELF_EMPLOYED', label: 'Self-employed' },
  { value: 'BUSINESS_OWNER', label: 'Business owner' }
];

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const validate = (values, alreadyVerified) => {
  const errors = {};

  // Once the server holds aadhaarLast4, the field may stay blank —
  // the applicant isn't asked to re-enter a number we no longer store.
  if (!alreadyVerified) {
    const digits = (values.aadhaarNumber || '').replace(/\s/g, '');
    if (!digits) errors.aadhaarNumber = 'Enter your Aadhaar number';
    else if (digits.length !== 12) errors.aadhaarNumber = 'Aadhaar must be 12 digits';
    else if (!isValidAadhaar(digits)) {
      errors.aadhaarNumber = 'Check this number — one or more digits look wrong';
    }
  }

  if (!PAN_REGEX.test((values.panNumber || '').toUpperCase())) {
    errors.panNumber = 'PAN must look like ABCDE1234F';
  }

  if (!values.employmentType) errors.employmentType = 'Select your employment type';
  if (!values.employerName?.trim()) errors.employerName = 'Enter your employer or business name';

  const years = Number(values.yearsAtEmployer);
  if (values.yearsAtEmployer === '' || Number.isNaN(years) || years < 0 || years > 60) {
    errors.yearsAtEmployer = 'Enter a number between 0 and 60';
  }

  const income = Number(values.monthlyIncome);
  if (!income || income <= 0) errors.monthlyIncome = 'Enter your monthly income';
  else if (income < 25000) errors.monthlyIncome = 'Minimum monthly income is ₹25,000';
  else if (income > 5000000) {
    errors.monthlyIncome = 'For incomes above ₹50 lakh a month, please contact us directly';
  }

  return errors;
};

// Display helper: 4321 8765 1234
const groupAadhaar = (value) =>
  value
    .replace(/\D/g, '')
    .slice(0, 12)
    .replace(/(\d{4})(?=\d)/g, '$1 ');

const KycStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const application = useSelector(selectApplication);
  const saving = useSelector(selectAppSaving);
  const serverError = useSelector(selectAppError);

  const alreadyVerified = Boolean(application?.aadhaarLast4);

  const [values, setValues] = useState({
    aadhaarNumber: '',
    panNumber: '',
    employmentType: '',
    employerName: '',
    yearsAtEmployer: '',
    monthlyIncome: '',
    voterId: '',
    passportNo: ''
  });
  const [errors, setErrors] = useState({});
  const [replaceAadhaar, setReplaceAadhaar] = useState(false);
  const extractedFields = useSelector(selectExtractedFields);
  const [extractedKeys, setExtractedKeys] = useState(new Set());

  // Hydrate from the saved application first, then layer extracted
  // values on top for any field the application doesn't already have.
  // One effect, so the two sources can't race and overwrite each other.
  useEffect(() => {
    if (!application) return;

    const base = {
      aadhaarNumber: '',
      panNumber: application.panNumber ?? '',
      employmentType: application.employmentType ?? '',
      employerName: application.employerName ?? '',
      yearsAtEmployer: application.yearsAtEmployer ?? '',
      monthlyIncome: application.monthlyIncome ?? '',
      voterId: application.voterId ?? '',
      passportNo: application.passportNo ?? ''
    };

    const applied = new Set();

    // Layer extraction on top — but only fill fields the saved
    // application left empty. Never overwrite already-saved data.
    if (extractedFields) {
      const KYC_KEYS = ['panNumber', 'employmentType', 'employerName', 'monthlyIncome'];
      KYC_KEYS.forEach((key) => {
        const extractedValue = extractedFields[key];
        const alreadySaved = base[key] !== '' && base[key] != null;

        if (!alreadySaved && extractedValue != null && extractedValue !== '') {
          base[key] =
            key === 'panNumber' ? String(extractedValue).toUpperCase() : String(extractedValue);
          applied.add(key);
        }
      });
    }

    setValues(base);
    setExtractedKeys(applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application, extractedFields]);

  const setField = (name) => (e) => {
    let value = e.target.value;
    if (name === 'aadhaarNumber') value = groupAadhaar(value);
    if (name === 'panNumber') value = value.toUpperCase();

    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (serverError) dispatch(clearApplicationError());
  };

  const showAadhaarInput = !alreadyVerified || replaceAadhaar;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validate(values, alreadyVerified && !replaceAadhaar);
    setErrors(found);
    if (Object.keys(found).length) {
      const firstErrorField = Object.keys(found)[0];
      document.getElementById(firstErrorField)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      document.getElementById(firstErrorField)?.focus();
      notify.error('Please complete the highlighted fields before continuing');
      return;
    }

    // Only send Aadhaar when the applicant actually typed one.
    const payload = { ...values };
    if (!showAadhaarInput || !payload.aadhaarNumber) {
      delete payload.aadhaarNumber;
    } else {
      payload.aadhaarNumber = payload.aadhaarNumber.replace(/\s/g, '');
    }
    if (!payload.voterId) delete payload.voterId;
    if (!payload.passportNo) delete payload.passportNo;

    const result = await dispatch(saveKycInfo({ id: application.id, values: payload }));

    if (saveKycInfo.fulfilled.match(result)) {
      navigate('/apply/loan');
    } else {
      notify.error(result.payload);
    }
  };

  return (
    <PageTransition>
      <ImportPanel />

      <StepCard
        title="Identity and employment"
        description="We use these details to verify who you are and assess affordability.">
        <form onSubmit={handleSubmit} noValidate>
          <FieldGrid>
            {showAadhaarInput ? (
              <Field
                label="Aadhaar number"
                htmlFor="aadhaarNumber"
                required
                hint="12 digits. We store only the last four."
                error={errors.aadhaarNumber}>
                <TextField
                  id="aadhaarNumber"
                  inputMode="numeric"
                  maxLength={14}
                  placeholder="0000 0000 0000"
                  value={values.aadhaarNumber}
                  onChange={setField('aadhaarNumber')}
                  error={errors.aadhaarNumber}
                />
              </Field>
            ) : (
              <Field label="Aadhaar number" hint="Only the last four digits are kept on file.">
                <div className="masked-value">
                  <span className="masked-value__text numeric">
                    XXXX XXXX {application.aadhaarLast4}
                  </span>
                  <button
                    type="button"
                    className="masked-value__action"
                    onClick={() => setReplaceAadhaar(true)}>
                    Change
                  </button>
                </div>
              </Field>
            )}

            <Field
              label="PAN number"
              htmlFor="panNumber"
              required
              hint="Format: ABCDE1234F"
              error={errors.panNumber}
              extracted={extractedKeys.has('panNumber')}>
              <TextField
                id="panNumber"
                maxLength={10}
                placeholder="ABCDE1234F"
                value={values.panNumber}
                onChange={setField('panNumber')}
                error={errors.panNumber}
              />
            </Field>

            <SectionRule label="Employment" />

            <Field
              label="Employment type"
              htmlFor="employmentType"
              required
              error={errors.employmentType}
              extracted={extractedKeys.has('employmentType')}>
              <SelectField
                id="employmentType"
                value={values.employmentType}
                onChange={setField('employmentType')}
                error={errors.employmentType}
                placeholder="Select"
                options={EMPLOYMENT_OPTIONS}
              />
            </Field>

            <Field
              label={values.employmentType === 'SALARIED' ? 'Employer name' : 'Business name'}
              htmlFor="employerName"
              required
              error={errors.employerName}
              extracted={extractedKeys.has('employerName')}>
              <TextField
                id="employerName"
                value={values.employerName}
                onChange={setField('employerName')}
                error={errors.employerName}
              />
            </Field>

            <Field
              label="Years at current employer"
              htmlFor="yearsAtEmployer"
              required
              error={errors.yearsAtEmployer}>
              <TextField
                id="yearsAtEmployer"
                inputMode="numeric"
                maxLength={2}
                value={values.yearsAtEmployer}
                onChange={setField('yearsAtEmployer')}
                error={errors.yearsAtEmployer}
              />
            </Field>

            <Field
              label="Monthly income (₹)"
              htmlFor="monthlyIncome"
              required
              hint="Net take-home, after deductions"
              error={errors.monthlyIncome}
              extracted={extractedKeys.has('monthlyIncome')}>
              <TextField
                id="monthlyIncome"
                inputMode="numeric"
                value={values.monthlyIncome}
                onChange={setField('monthlyIncome')}
                error={errors.monthlyIncome}
              />
            </Field>

            <SectionRule label="Optional identification" />

            <Field label="Voter ID" htmlFor="voterId" hint="Optional">
              <TextField id="voterId" value={values.voterId} onChange={setField('voterId')} />
            </Field>

            <Field label="Passport number" htmlFor="passportNo" hint="Optional">
              <TextField
                id="passportNo"
                value={values.passportNo}
                onChange={setField('passportNo')}
              />
            </Field>
          </FieldGrid>

          {serverError && (
            <p className="form-error" role="alert">
              {serverError}
            </p>
          )}

          <StepActions onBack={() => navigate('/apply/personal')}>
            <Button type="submit" loading={saving}>
              {saving ? 'Saving…' : 'Continue to loan details →'}
            </Button>
          </StepActions>
        </form>
      </StepCard>
    </PageTransition>
  );
};

export default KycStep;
